import React, { useState, useEffect, useMemo } from 'react';
import { Box, Container, Typography, Paper, Grid, Tabs, Tab, Skeleton } from '@mui/material';
import { People, SportsEsports, Whatshot, EmojiEvents } from '@mui/icons-material';
import NavBar from '../../../Components/NavBar/navbar';
import { API_ENDPOINTS } from '../../../utils/config';
import { THEME } from '../../../theme';
import GameLeaderboardView from './GameLeaderboardView';
import UserProfileView from './UserProfileView';
import AgentDetailView from './AgentDetailView';
import HighlightsCarousel from './HighlightsCarousel';
import LiveSessionsPanel from './LiveSessionsPanel';
import ArcadeHubNav, { ARCADE_SECTIONS } from '../ArcadeHubNav';
import { gamesData } from '../../../Components/Arcade/gamesConfig';
import { buildGameNameMap, gameDisplayName } from './displayHelpers';
import { COLOR } from '../../../Components/Arcade/ui/tokens';
import UiCard from '../../../Components/Arcade/ui/Card';
import usePageTitle from '../../../Components/Arcade/ui/usePageTitle';

const StatCard = ({ icon, label, value, loading, color }) => (
    <UiCard sx={{ p: 2.5, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
                width: 44, height: 44, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: `${color}22`, color,
            }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" sx={{ color: THEME.secondary, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {label}
                </Typography>
                {loading ? (
                    <Skeleton variant="text" width={80} sx={{ bgcolor: 'rgba(255,255,255,0.08)' }} />
                ) : (
                    <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, whiteSpace: 'nowrap' }}>
                        {value}
                    </Typography>
                )}
            </Box>
        </Box>
    </UiCard>
);

/**
 * Dual-purpose: embedded as the "Dashboard" tab inside the Arcade hub (embedded=true,
 * skips its own NavBar/page chrome) or standalone page chrome for rare direct use.
 * Prefer /arcade?section=dashboard — /arcade/dashboard redirects there.
 */
const DashboardHome = ({ embedded = false }) => {
    usePageTitle(embedded ? null : 'Dashboard');
    const [tab, setTab] = useState('games');
    const [leaderboard, setLeaderboard] = useState([]);
    const [leaderboardLoading, setLeaderboardLoading] = useState(true);
    const [leaderboardError, setLeaderboardError] = useState(null);
    const [games, setGames] = useState([]);

    useEffect(() => {
        let mounted = true;
        async function fetchOverviewData() {
            setLeaderboardLoading(true);
            setLeaderboardError(null);
            try {
                const res = await fetch(API_ENDPOINTS.LEADERBOARD());
                if (!res.ok) throw new Error(`status ${res.status}`);
                const data = await res.json();
                const rows = data.leaderboard || data.entries || (Array.isArray(data) ? data : []);
                if (mounted) setLeaderboard(rows);
            } catch (e) {
                console.error('Failed to load leaderboard:', e);
                if (mounted) setLeaderboardError('Failed to load');
                if (mounted) setLeaderboard([]);
            }
            if (mounted) setLeaderboardLoading(false);
        }
        async function fetchGames() {
            try {
                const res = await fetch(API_ENDPOINTS.GAMES_LIST);
                if (!res.ok) return;
                const data = await res.json();
                if (mounted) setGames(Array.isArray(data) ? data : (data.games || []));
            } catch (e) {
                // non-fatal, leave games empty
            }
        }
        fetchOverviewData();
        fetchGames();
        return () => { mounted = false; };
    }, []);

    const gameNameMap = useMemo(() => buildGameNameMap([...games, ...gamesData]), [games]);

    const stats = useMemo(() => {
        const totalRuns = leaderboard.length;
        const distinctUsers = new Set(
            leaderboard.map((e) => e.user_id || e.username || e.player_id).filter(Boolean)
        ).size;
        const gameCounts = {};
        leaderboard.forEach((e) => {
            const g = e.game_id || e.game;
            if (!g) return;
            gameCounts[g] = (gameCounts[g] || 0) + 1;
        });
        let mostActiveGameId = null;
        let maxCount = 0;
        Object.entries(gameCounts).forEach(([g, c]) => {
            if (c > maxCount) {
                maxCount = c;
                mostActiveGameId = g;
            }
        });
        const mostActiveGame = mostActiveGameId
            ? gameDisplayName(mostActiveGameId, gameNameMap)
            : '—';
        return { totalRuns, distinctUsers, mostActiveGame };
    }, [leaderboard, gameNameMap]);

    const body = (
        <>
            {!embedded && (
                <Box sx={{ textAlign: 'center', mb: 5 }}>
                    <Typography variant="h2" sx={{ color: 'white', fontWeight: 800, mb: 1, letterSpacing: '-1px' }}>
                        Arcade <span style={{ color: THEME.primary }}>Dashboard</span>
                    </Typography>
                    <Typography variant="body1" sx={{ color: THEME.secondary }}>
                        Explore leaderboards, user profiles, agents, and standout runs across all games
                    </Typography>
                </Box>
            )}

            {leaderboardError && (
                <Box role="alert" sx={{ p: 2, mb: 3, bgcolor: 'rgba(239, 71, 67, 0.08)', border: `1px solid ${COLOR.danger}`, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ color: COLOR.text }}>
                        Some scores can't be shown right now. Everything else is up to date. Please try again in a moment.
                    </Typography>
                </Box>
            )}

            <LiveSessionsPanel />

            <Grid container spacing={3} sx={{ mb: 5 }}>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<People />} label="Distinct Users" value={stats.distinctUsers} loading={leaderboardLoading} color={THEME.primary} />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<SportsEsports />} label="Total Runs / Submissions" value={stats.totalRuns} loading={leaderboardLoading} color="#3b82f6" />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <StatCard icon={<Whatshot />} label="Most Active Game" value={stats.mostActiveGame} loading={leaderboardLoading} color="#fbbf24" />
                </Grid>
            </Grid>

            <Paper sx={{ bgcolor: THEME.cardBg, border: `1px solid ${THEME.border}`, borderRadius: 3, overflow: 'hidden' }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        borderBottom: `1px solid ${THEME.border}`,
                        '& .MuiTab-root': { color: THEME.secondary, textTransform: 'none', fontWeight: 600 },
                        '& .Mui-selected': { color: `${THEME.primary} !important` },
                        '& .MuiTabs-indicator': { bgcolor: THEME.primary },
                    }}
                >
                    <Tab value="games" label="Games" icon={<SportsEsports fontSize="small" />} iconPosition="start" />
                    <Tab value="users" label="Users" icon={<People fontSize="small" />} iconPosition="start" />
                    <Tab value="agents" label="Agents" icon={<EmojiEvents fontSize="small" />} iconPosition="start" />
                    <Tab value="highlights" label="Highlights" icon={<Whatshot fontSize="small" />} iconPosition="start" />
                </Tabs>

                <Box sx={{ p: 3 }}>
                    {tab === 'games' && (
                        <GameLeaderboardView games={games} leaderboard={leaderboard} loading={leaderboardLoading} />
                    )}
                    {tab === 'users' && (
                        <UserProfileView games={games} leaderboard={leaderboard} loading={leaderboardLoading} />
                    )}
                    {tab === 'agents' && (
                        <AgentDetailView games={games} leaderboard={leaderboard} loading={leaderboardLoading} />
                    )}
                    {tab === 'highlights' && <HighlightsCarousel />}
                </Box>
            </Paper>
        </>
    );

    if (embedded) {
        return body;
    }

    return (
        <Box sx={{ bgcolor: THEME.bg, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavBar />
            <Box sx={{ flexGrow: 1, pt: 12, pb: 8 }}>
                <Container maxWidth="xl">
                    <ArcadeHubNav activeSection={ARCADE_SECTIONS.dashboard} />
                    {body}
                </Container>
            </Box>
        </Box>
    );
};

export default DashboardHome;
